import React, { useState, useEffect } from 'react';
import { useTranslation } from '../context/LanguageContext';
import { apiService } from '../services/api';
import { BroadcastEmailRequest, EmailAttachmentResponse, UserResponse } from '../types/api';
import './BroadcastEmail.css';

const MAX_ATTACHMENT_TOTAL_BYTES = 10 * 1024 * 1024;

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

interface BroadcastEmailProps {
  onUpdate?: () => Promise<void>;
}

const BroadcastEmail: React.FC<BroadcastEmailProps> = ({ onUpdate }) => {
  const { t } = useTranslation();
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [audienceMode, setAudienceMode] = useState<'active' | 'all' | 'specific'>('active');
  const [allClientsList, setAllClientsList] = useState<UserResponse[]>([]);
  const [selectedClientIds, setSelectedClientIds] = useState<number[]>([]);
  const [clientSearch, setClientSearch] = useState('');
  const [loadingClients, setLoadingClients] = useState(false);
  const [scheduleOption, setScheduleOption] = useState<'now' | 'later'>('now');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const [attachments, setAttachments] = useState<EmailAttachmentResponse[]>([]);
  const [isUploadingAttachments, setIsUploadingAttachments] = useState(false);
  const [attachmentError, setAttachmentError] = useState('');

  const clearMessages = () => {
    setSuccessMessage('');
    setErrorMessage('');
    setErrors([]);
  };

  const handleSubjectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length <= 200) {
      setSubject(value);
      clearMessages();
    }
  };

  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length <= 10000) {
      setMessage(value);
      clearMessages();
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    e.target.value = '';
    if (selectedFiles.length === 0) return;

    const existingSize = attachments.reduce((sum, a) => sum + a.fileSize, 0);
    const newSize = selectedFiles.reduce((sum, f) => sum + f.size, 0);
    if (existingSize + newSize > MAX_ATTACHMENT_TOTAL_BYTES) {
      setAttachmentError(t('attachmentsTooLarge'));
      return;
    }

    setAttachmentError('');
    setIsUploadingAttachments(true);
    try {
      const response = await apiService.uploadBroadcastAttachments(selectedFiles);
      if (response.success && response.data) {
        setAttachments(prev => [...prev, ...response.data!]);
      } else {
        setAttachmentError(response.message || t('attachmentUploadFailed'));
      }
    } catch (err) {
      setAttachmentError(t('attachmentUploadFailed'));
    } finally {
      setIsUploadingAttachments(false);
    }
  };

  const handleRemoveAttachment = async (id: number) => {
    setAttachments(prev => prev.filter(a => a.id !== id));
    try {
      await apiService.deleteBroadcastAttachment(id);
    } catch (err) {
      // Attachment row becomes orphaned server-side if this fails - not user-facing,
      // it just won't be referenced by any future send.
    }
  };

  const handleAudienceModeChange = (mode: 'active' | 'all' | 'specific') => {
    setAudienceMode(mode);
    clearMessages();
  };

  const handleClientToggle = (clientId: number) => {
    setSelectedClientIds(prev =>
      prev.includes(clientId) ? prev.filter(id => id !== clientId) : [...prev, clientId]
    );
  };

  const filteredClients = allClientsList.filter(c =>
    clientSearch === '' ||
    c.name.toLowerCase().includes(clientSearch.toLowerCase()) ||
    c.email.toLowerCase().includes(clientSearch.toLowerCase())
  );

  const allFilteredSelected =
    filteredClients.length > 0 &&
    filteredClients.every(c => selectedClientIds.includes(c.id));

  const handleToggleSelectAll = () => {
    const filteredIds = filteredClients.map(c => c.id);
    if (allFilteredSelected) {
      setSelectedClientIds(prev => prev.filter(id => !filteredIds.includes(id)));
    } else {
      setSelectedClientIds(prev => [...prev, ...filteredIds.filter(id => !prev.includes(id))]);
    }
  };

  const handleScheduleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.checked ? 'later' : 'now';
    setScheduleOption(value);
    if (value === 'now') {
      setScheduledDate('');
      setScheduledTime('');
    }
    clearMessages();
  };

  const handleScheduledDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setScheduledDate(e.target.value);
    clearMessages();
  };

  const handleScheduledTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setScheduledTime(e.target.value);
    clearMessages();
  };

  useEffect(() => {
    if (audienceMode !== 'specific') return;
    if (allClientsList.length > 0) return;
    setLoadingClients(true);
    apiService.getClients('').then(response => {
      if (response.data) {
        setAllClientsList(response.data);
      }
    }).catch(() => {
      // silently fail
    }).finally(() => {
      setLoadingClients(false);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audienceMode]);

  const scheduledDateTimeInput = scheduledDate && scheduledTime
    ? `${scheduledDate}T${scheduledTime}`
    : '';

  const getLocalDateString = (date: Date): string => {
    const pad = (num: number) => String(num).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  };

  const getLocalTimeString = (date: Date): string => {
    const pad = (num: number) => String(num).padStart(2, '0');
    return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  };

  const now = new Date();
  const minDate = getLocalDateString(now);
  const minTime = scheduledDate === minDate ? getLocalTimeString(now) : undefined;

  const formatScheduledSendTime = (value: string): string | null => {
    if (!value) {
      return null;
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return null;
    }

    const pad = (num: number, size: number = 2) => String(num).padStart(size, '0');
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());
    const microseconds = pad(date.getMilliseconds() * 1000, 6);

    const offsetMinutes = -date.getTimezoneOffset();
    const offsetSign = offsetMinutes >= 0 ? '+' : '-';
    const offsetAbs = Math.abs(offsetMinutes);
    const offsetHours = pad(Math.floor(offsetAbs / 60));
    const offsetRemainder = offsetAbs % 60;
    const offset = offsetRemainder === 0
      ? `${offsetSign}${offsetHours}`
      : `${offsetSign}${offsetHours}${pad(offsetRemainder)}`;

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${microseconds}${offset}`;
  };

  const validateForm = (): boolean => {
    const validationErrors: string[] = [];

    if (!subject.trim()) {
      validationErrors.push(t('subjectRequired'));
    } else if (subject.length > 200) {
      validationErrors.push(t('subjectTooLong'));
    }

    if (!message.trim()) {
      validationErrors.push(t('messageRequired'));
    } else if (message.length > 10000) {
      validationErrors.push(t('messageTooLong'));
    }

    if (scheduleOption === 'later') {
      if (!scheduledDate || !scheduledTime) {
        validationErrors.push(t('scheduledSendTimeRequired'));
      } else if (!formatScheduledSendTime(scheduledDateTimeInput)) {
        validationErrors.push(t('scheduledSendTimeInvalid'));
      } else {
        const scheduledDateTime = new Date(scheduledDateTimeInput);
        if (!Number.isNaN(scheduledDateTime.getTime()) && scheduledDateTime <= new Date()) {
          validationErrors.push(t('scheduledSendTimeFuture'));
        }
      }
    }

    if (audienceMode === 'specific' && selectedClientIds.length === 0) {
      validationErrors.push(t('noClientsSelectedError'));
    }

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      setErrorMessage(t('fixValidationErrors'));
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearMessages();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const scheduledSendTime = scheduleOption === 'later'
        ? formatScheduledSendTime(scheduledDateTimeInput)
        : undefined;

      const request: BroadcastEmailRequest = {
        subject: subject.trim(),
        message: message.trim(),
        activeClientsOnly: audienceMode === 'active',
        ...(audienceMode === 'specific' ? { targetClientIds: selectedClientIds } : {}),
        ...(scheduledSendTime ? { scheduledSendTime } : {}),
        ...(attachments.length > 0 ? { attachmentIds: attachments.map(a => a.id) } : {})
      };

      const response = await apiService.sendBroadcastEmail(request);

      if (response.success) {
        setSuccessMessage(response.message);
        // Clear form after successful send
        setSubject('');
        setMessage('');
        setAudienceMode('active');
        setSelectedClientIds([]);
        setClientSearch('');
        setScheduleOption('now');
        setScheduledDate('');
        setScheduledTime('');
        setAttachments([]);
        setAttachmentError('');

        if (onUpdate) {
          await onUpdate();
        }
      } else {
        setErrorMessage(response.message || t('failedToSendEmail'));
        if (response.errors && response.errors.length > 0) {
          setErrors(response.errors);
        }
      }
    } catch (err: any) {
      console.error('Error sending broadcast email:', err);
      setErrorMessage(t('errorSendingEmail'));
      
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    attachments.forEach(a => {
      apiService.deleteBroadcastAttachment(a.id).catch(() => {});
    });
    setSubject('');
    setMessage('');
    setAudienceMode('active');
    setSelectedClientIds([]);
    setClientSearch('');
    setScheduleOption('now');
    setScheduledDate('');
    setScheduledTime('');
    setAttachments([]);
    setAttachmentError('');
    clearMessages();
  };

  const insertUserNameVariable = () => {
    const textarea = document.getElementById('message-textarea') as HTMLTextAreaElement;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newMessage = message.substring(0, start) + '{UserName}' + message.substring(end);
      setMessage(newMessage);
      
      // Set cursor position after the inserted variable
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + 10;
        textarea.focus();
      }, 0);
    } else {
      setMessage(prev => prev + '{UserName}');
    }
    clearMessages();
  };

  return (
    <div className="broadcast-email">
      <div className="broadcast-email-header">
        <h2>{t('broadcastEmailTitle')}</h2>
        <p>{t('sendPersonalizedEmails')}</p>
      </div>

      {successMessage && (
        <div className="success-message">
          {successMessage}
        </div>
      )}

      {errorMessage && (
        <div className="error-message">
          <strong>{errorMessage}</strong>
          {errors.length > 0 && (
            <ul className="error-list">
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit} className="broadcast-email-form">
        <div className="form-group">
          <label htmlFor="subject">{t('subject')} <span className="required">*</span></label>
          <input
            type="text"
            id="subject"
            value={subject}
            onChange={handleSubjectChange}
            placeholder={t('enterEmailSubject')}
            className="form-input"
            disabled={isLoading}
            maxLength={200}
          />
          <div className="character-count">
            {subject.length}/200 {t('characterCount')}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="message-textarea">
            {t('message')} <span className="required">*</span>
            <button
              type="button"
              onClick={insertUserNameVariable}
              className="variable-button"
              disabled={isLoading}
              title={t('insertUserNameVariable')}
            >
              {t('insertUserName')}
            </button>
          </label>
          <textarea
            id="message-textarea"
            value={message}
            onChange={handleMessageChange}
            placeholder={t('enterMessagePlaceholder')}
            className="form-textarea"
            disabled={isLoading}
            rows={8}
            maxLength={10000}
          />
          <div className="character-count">
            {message.length}/10000 {t('characters')}
          </div>
          <div className="message-note">
            <strong>{t('note')}:</strong> {t('lineBreaksNote')}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="attachments-input">{t('attachments')}</label>
          <input
            type="file"
            id="attachments-input"
            multiple
            onChange={handleFileSelect}
            disabled={isLoading || isUploadingAttachments}
          />
          {isUploadingAttachments && (
            <div className="attachment-uploading">
              <span className="loading-spinner"></span> {t('uploadingAttachment')}
            </div>
          )}
          {attachmentError && (
            <div className="attachment-error">{attachmentError}</div>
          )}
          {attachments.length > 0 && (
            <ul className="attachment-list">
              {attachments.map(a => (
                <li key={a.id} className="attachment-item">
                  <span className="attachment-name">{a.fileName}</span>
                  <span className="attachment-size">{formatFileSize(a.fileSize)}</span>
                  <button
                    type="button"
                    className="attachment-remove"
                    onClick={() => handleRemoveAttachment(a.id)}
                    disabled={isLoading}
                    aria-label={`Remove ${a.fileName}`}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="message-note">
            <strong>{t('note')}:</strong> {t('attachmentsSizeNote')}
          </div>
        </div>

        <div className="form-group">
          <label className="schedule-label">{t('audience')}</label>
          <div className="audience-options">
            <button
              type="button"
              className={`audience-option${audienceMode === 'active' ? ' selected' : ''}`}
              onClick={() => handleAudienceModeChange('active')}
              disabled={isLoading}
            >
              {t('activeClientsOnly')}
            </button>
            <button
              type="button"
              className={`audience-option${audienceMode === 'all' ? ' selected' : ''}`}
              onClick={() => handleAudienceModeChange('all')}
              disabled={isLoading}
            >
              {t('allClients')}
            </button>
            <button
              type="button"
              className={`audience-option${audienceMode === 'specific' ? ' selected' : ''}`}
              onClick={() => handleAudienceModeChange('specific')}
              disabled={isLoading}
            >
              {t('specificClients')}
            </button>
          </div>
          <p className="toggle-current">
            {audienceMode === 'active' && t('selectedActiveClientsOnly')}
            {audienceMode === 'all' && t('selectedAllClients')}
            {audienceMode === 'specific' && t('audienceSpecificClients')}
          </p>

          {audienceMode === 'specific' && (
            <div className="client-selector">
              <div className="client-selector-toolbar">
                <input
                  type="text"
                  className="form-input client-search-input"
                  placeholder={t('searchClientsPlaceholder')}
                  value={clientSearch}
                  onChange={e => setClientSearch(e.target.value)}
                  disabled={isLoading || loadingClients}
                />
                <div className="client-selector-actions">
                  <button
                    type="button"
                    className="selector-action-button"
                    onClick={handleToggleSelectAll}
                    disabled={isLoading || loadingClients || filteredClients.length === 0}
                  >
                    {allFilteredSelected ? t('clearSelection') : t('selectAll')}
                  </button>
                  {selectedClientIds.length > 0 && (
                    <button
                      type="button"
                      className="selector-action-button danger"
                      onClick={() => setSelectedClientIds([])}
                      disabled={isLoading}
                    >
                      {t('clearSelection')}
                    </button>
                  )}
                </div>
              </div>

              {loadingClients ? (
                <div className="client-list-loading">
                  <span className="loading-spinner"></span>
                  {t('loadingClients')}
                </div>
              ) : (
                <div className="client-list">
                  {filteredClients.length === 0 ? (
                    <div className="client-list-empty">{t('noClientsFoundGeneral')}</div>
                  ) : (
                    filteredClients.map(client => (
                      <label key={client.id} className="client-item">
                        <input
                          type="checkbox"
                          checked={selectedClientIds.includes(client.id)}
                          onChange={() => handleClientToggle(client.id)}
                          disabled={isLoading}
                        />
                        <span className="client-item-info">
                          <span className="client-item-name">{client.name}</span>
                          <span className="client-item-email">{client.email}</span>
                        </span>
                      </label>
                    ))
                  )}
                </div>
              )}

              {selectedClientIds.length > 0 && (() => {
                const selectedClients = allClientsList.filter(c => selectedClientIds.includes(c.id));
                return (
                  <>
                    <p className="client-selection-count">
                      {selectedClientIds.length} {t('clientsSelected')}
                    </p>
                    <div className="selected-clients-tags">
                      {selectedClients.map(c => (
                        <span key={c.id} className="selected-client-tag">
                          {c.name}
                          <button
                            type="button"
                            className="selected-client-tag-remove"
                            onClick={() => handleClientToggle(c.id)}
                            disabled={isLoading}
                            aria-label={`Remove ${c.name}`}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </div>

        <div className="form-group">
          <label className="schedule-label">{t('scheduleBroadcastQuestion')}</label>
          <div className="schedule-toggle">
            <span className={scheduleOption === 'now' ? 'toggle-text active' : 'toggle-text'}>
              {t('sendNow')}
            </span>
            <label className="switch" aria-label={t('scheduleBroadcastQuestion')}>
              <input
                type="checkbox"
                checked={scheduleOption === 'later'}
                onChange={handleScheduleToggle}
                disabled={isLoading}
              />
              <span className="slider"></span>
            </label>
            <span className={scheduleOption === 'later' ? 'toggle-text active' : 'toggle-text'}>
              {t('scheduleForLater')}
            </span>
          </div>
          <p className="toggle-current">
            {scheduleOption === 'later' ? t('selectedScheduleForLater') : t('selectedSendNow')}
          </p>
          {scheduleOption === 'later' && (
            <div className="schedule-time">
              <label>{t('scheduledSendTime')}</label>
              <div className="schedule-inputs">
                <input
                  type="date"
                  value={scheduledDate}
                  onChange={handleScheduledDateChange}
                  disabled={isLoading}
                  className="form-input"
                  min={minDate}
                />
                <input
                  type="time"
                  value={scheduledTime}
                  onChange={handleScheduledTimeChange}
                  disabled={isLoading}
                  className="form-input"
                  step="1"
                  min={minTime}
                />
              </div>
              <p className="schedule-helper">{t('scheduledSendTimeHelper')}</p>
            </div>
          )}
        </div>

        <div className="variable-info">
          <h4>{t('availableVariables')}:</h4>
          <ul>
            <li><code>{'{UserName}'}</code> - {t('variableWillBeReplaced')}</li>
          </ul>
          <p className="variable-example">
            <strong>{t('variableExample')}:</strong> "Dear {'{UserName}'}, welcome to our gym!" {t('variableExampleText')} 
            "Dear John Doe, welcome to our gym!" for each client.
          </p>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={handleClear}
            className="clear-button"
            disabled={isLoading}
          >
            {t('clearForm')}
          </button>
          <button
            type="submit"
            className="send-button"
            disabled={isLoading || isUploadingAttachments || !subject.trim() || !message.trim()}
          >
            {isLoading ? (
              <span>
                <span className="loading-spinner"></span>
                {t('sendingEmail')}
              </span>
            ) : (
              t('sendBroadcastEmail')
            )}
          </button>
        </div>
      </form>

      <div className="email-preview">
        <h3>{t('preview')}</h3>
        <div className="preview-content">
          <div className="preview-subject">
            <strong>{t('subject')}:</strong> {subject || t('noSubjectEntered')}
          </div>
          <div className="preview-message">
            <strong>{t('message')}:</strong>
            <div className="preview-body">
              {message 
                ? message.replace(/\n/g, '<br>').replace(/{UserName}/g, '<span class="preview-variable">John Doe</span>')
                : t('noMessageEntered')
              }
            </div>
          </div>
          <div className="preview-audience">
            <strong>{t('audience')}:</strong>{' '}
            {audienceMode === 'active' && t('activeClientsOnly')}
            {audienceMode === 'all' && t('allClients')}
            {audienceMode === 'specific' && `${t('specificClients')} (${selectedClientIds.length} ${t('clientsSelected')})`}
          </div>
          <div className="preview-audience">
            <strong>{t('schedule')}:</strong> {scheduleOption === 'later' && scheduledDateTimeInput ? t('scheduledFor') : t('sendImmediately')}
            {scheduleOption === 'later' && scheduledDateTimeInput && (
              <span className="preview-schedule">{scheduledDateTimeInput.replace('T', ' ')}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BroadcastEmail; 